import { cn } from "@/lib/utils";
import {
  EDITIONS,
  formatDayLong,
  formatPrice,
  formatRaceTime,
  topTipWins,
  topTipWon,
} from "@/utils/race-results";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Cloth, DayStatus, EditionPills, EmptyState } from "./parts";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const pad2 = (n) => String(n).padStart(2, "0");

// Share of races won by a Lexi Top Tip (any edition) → cell shade
const SHADES = [
  {
    min: 0.45,
    cls: "border-brand-pink bg-brand-pink text-white",
    label: "45%+",
  },
  {
    min: 0.3,
    cls: "border-[#F0A7D5] bg-[#FFE6F6] text-gray-900",
    label: "30–44%",
  },
  {
    min: 0.0001,
    cls: "border-pink-200 bg-[#FDF2F8] text-gray-900",
    label: "under 30%",
  },
  { min: 0, cls: "border-gray-100 bg-gray-50 text-gray-900", label: "0%" },
];
const shadeFor = (rate) =>
  SHADES.find((s) => rate >= s.min) || SHADES[SHADES.length - 1];

/** Month calendar shaded by how Lexi's Top Tips went; pick a day to list its races. */
export default function ResultsCalendar({
  year,
  month,
  days,
  selectedIso,
  onSelect,
  onPrev,
  onNext,
  canPrev,
  canNext,
  todayIso,
}) {
  const byIso = new Map(days.map((d) => [d.iso, d]));
  const lead = (new Date(Date.UTC(year, month - 1, 1)).getUTCDay() + 6) % 7;
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const monthLabel = new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(
    "en-AU",
    {
      timeZone: "UTC",
      month: "long",
      year: "numeric",
    },
  );

  const selected = byIso.get(selectedIso);
  const list = selected?.status === "success" ? selected.races : [];

  return (
    <div className="grid items-start gap-4 lg:grid-cols-[minmax(300px,400px)_minmax(0,1fr)]">
      <div className="rounded-xl border border-gray-100 bg-white p-3.5 shadow-lg shadow-black/[0.04]">
        <div className="mb-2.5 flex items-center justify-between">
          <button
            type="button"
            onClick={onPrev}
            disabled={!canPrev}
            aria-label="Previous month"
            className="grid h-8 w-8 place-items-center rounded-lg border border-gray-200 text-gray-600 disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <b className="text-base font-extrabold">{monthLabel}</b>
          <button
            type="button"
            onClick={onNext}
            disabled={!canNext}
            aria-label="Next month"
            className="grid h-8 w-8 place-items-center rounded-lg border border-gray-200 text-gray-600 disabled:opacity-40"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1">
          {WEEKDAYS.map((w) => (
            <span
              key={w}
              className="pb-1.5 pt-1 text-center text-[11px] font-bold uppercase text-gray-400"
            >
              {w}
            </span>
          ))}
          {Array.from({ length: lead }, (_, i) => (
            <span key={`lead-${i}`} />
          ))}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const iso = `${year}-${pad2(month)}-${pad2(i + 1)}`;
            const day = byIso.get(iso);
            const base =
              "grid aspect-square min-w-0 content-between rounded-md border p-1 text-left text-xs sm:rounded-lg sm:p-1.5";
            if (iso === todayIso) {
              return (
                <span
                  key={iso}
                  className={cn(
                    base,
                    "border-dashed border-brand-pink text-[#C8007E]",
                  )}
                >
                  <b>{i + 1}</b>
                  <span className="text-[10.5px] font-bold">Today</span>
                </span>
              );
            }
            if (!day) {
              return (
                <span
                  key={iso}
                  className={cn(base, "border-gray-100 text-gray-300")}
                >
                  <b>{i + 1}</b>
                </span>
              );
            }
            if (day.status !== "success" || !day.races.length) {
              return (
                <span
                  key={iso}
                  className={cn(
                    base,
                    "border-gray-100 text-gray-400",
                    day.status === "pending" && "animate-pulse",
                  )}
                >
                  <b>{i + 1}</b>
                </span>
              );
            }
            const wins = day.races.filter(topTipWon).length;
            const shade = shadeFor(wins / day.races.length);
            return (
              <button
                key={iso}
                type="button"
                onClick={() => onSelect(iso)}
                aria-pressed={iso === selectedIso}
                aria-label={`${formatDayLong(iso)}: a Lexi Top Tip won ${wins} of ${day.races.length} races`}
                className={cn(
                  base,
                  shade.cls,
                  iso === selectedIso &&
                    "shadow-[0_0_0_2px_#fff,0_0_0_4px_#0a0a0a]",
                )}
              >
                <b>{i + 1}</b>
                <span className="whitespace-nowrap text-[9.5px] font-bold tabular-nums tracking-tight sm:text-[10.5px] sm:tracking-normal">
                  {wins}/{day.races.length}
                </span>
              </button>
            );
          })}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
          <span>Races won by a Lexi Top Tip:</span>
          {[...SHADES].reverse().map((s) => (
            <span key={s.label} className="inline-flex items-center gap-1">
              <i
                className={cn(
                  "inline-block h-3 w-4 rounded-[3px] border",
                  s.cls,
                )}
              />
              {s.label}
            </span>
          ))}
        </div>
      </div>

      <div className="min-w-0 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg shadow-black/[0.04]">
        <div className="grid gap-1 border-b border-gray-100 px-4 py-3.5">
          <h3 className="text-[17px] font-extrabold tracking-tight">
            {selectedIso ? formatDayLong(selectedIso) : "Pick a day"}
          </h3>
          {list.length > 0 && (
            <p className="flex flex-wrap gap-1.5 text-xs tabular-nums text-gray-600">
              <span className="rounded-md border border-gray-100 bg-gray-50 px-2 py-0.5">
                {list.length} races
              </span>
              {EDITIONS.filter(({ key }) =>
                list.some((r) => r[key]?.length),
              ).map(({ key, label }) => (
                <span
                  key={key}
                  className="rounded-md border border-gray-100 bg-gray-50 px-2 py-0.5"
                >
                  {label} Top Tip{" "}
                  <b className="text-[#C8007E]">{topTipWins(list, key)}</b> won
                </span>
              ))}
            </p>
          )}
        </div>
        {selected && selected.status !== "success" ? (
          <div className="p-3.5">
            <DayStatus day={selected} label={formatDayLong(selectedIso)} />
          </div>
        ) : list.length ? (
          list.map((race) => (
            <div
              key={race.id}
              className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2.5 gap-y-1 border-t border-gray-100 px-4 py-2.5 text-[13.5px] [grid-template-areas:'rc_p'_'w_w'_'o_o'] first:border-t-0 md:grid-cols-[62px_minmax(0,0.9fr)_minmax(0,1.2fr)_56px_auto] md:[grid-template-areas:'t_rc_w_p_o']"
            >
              <span className="hidden text-[12.5px] tabular-nums text-gray-500 [grid-area:t] md:block">
                {formatRaceTime(race.start)}
              </span>
              <b className="min-w-0 truncate [grid-area:rc]">
                {race.track} R{race.race}
              </b>
              <span className="flex min-w-0 items-center gap-1.5 font-bold [grid-area:w]">
                <Cloth no={race.winner.no} />
                <span className="truncate">{race.winner.name}</span>
              </span>
              <span className="text-right font-semibold tabular-nums [grid-area:p]">
                {formatPrice(race.winner.sp)}
              </span>
              <EditionPills
                race={race}
                className="[grid-area:o] md:justify-end"
              />
            </div>
          ))
        ) : (
          <div className="p-3.5">
            <EmptyState>No races for this day with these filters.</EmptyState>
          </div>
        )}
      </div>
    </div>
  );
}
